// frontend/src/components/admin/SuperAdminUsersTable.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Alert,
    CircularProgress,
    Stack,
    Divider
} from '@mui/material';
import { format } from 'date-fns';
import apiClient from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';

const VERIFICATION_METHODS = [
    { value: 'standard-email', label: 'Standard email' },
    { value: 'enhanced-email', label: 'Enhanced email' },
    { value: 'enhanced-voice', label: 'Enhanced voice' },
    { value: 'automatic-12mo', label: 'Automatic 12-month' }
];

const SuperAdminUsersTable = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Simple action state
    const [actionTarget, setActionTarget] = useState(null); // { user, action: 'deactivate'|'reactivate' }
    const [actionBusy, setActionBusy] = useState(false);

    // Delete flow state
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [deleteForm, setDeleteForm] = useState({
        authorizedBy: '',
        authorizedByEmail: '',
        verificationMethod: 'standard-email',
        reason: ''
    });
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [deleteResult, setDeleteResult] = useState(null);

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const { data } = await apiClient.get('/api/admin/users');
            setUsers(data.users || []);
        } catch (err) {
            setError(err.response?.data?.msg || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const isSelf = (u) => currentUser && (currentUser._id === u._id || currentUser.id === u._id);

    const runSimpleAction = async () => {
        if (!actionTarget) return;
        setActionBusy(true);
        try {
            const path = actionTarget.action === 'deactivate'
                ? `/api/admin/users/${actionTarget.user._id}/deactivate`
                : `/api/admin/users/${actionTarget.user._id}/reactivate`;
            await apiClient.patch(path);
            setActionTarget(null);
            await fetchUsers();
        } catch (err) {
            setError(err.response?.data?.msg || 'Action failed');
            setActionTarget(null);
        } finally {
            setActionBusy(false);
        }
    };

    const openDelete = async (user) => {
        setDeleteTarget(user);
        setPreviewData(null);
        setPreviewError(null);
        setDeleteError(null);
        setDeleteResult(null);
        setDeleteForm({
            authorizedBy: currentUser?.name || '',
            authorizedByEmail: currentUser?.email || '',
            verificationMethod: 'standard-email',
            reason: ''
        });
        setPreviewLoading(true);
        try {
            const { data } = await apiClient.post(`/api/admin/users/${user._id}/delete-preview`);
            setPreviewData(data);
        } catch (err) {
            setPreviewError(err.response?.data?.msg || 'Failed to load deletion preview');
        } finally {
            setPreviewLoading(false);
        }
    };

    const closeDelete = () => {
        setDeleteTarget(null);
        setPreviewData(null);
        setDeleteResult(null);
        setDeleteError(null);
    };

    const canSubmitDelete = () =>
        deleteForm.authorizedBy.trim() &&
        deleteForm.authorizedByEmail.trim() &&
        deleteForm.verificationMethod &&
        deleteForm.reason.trim();

    const submitDelete = async () => {
        if (!deleteTarget || !canSubmitDelete()) return;
        setDeleteBusy(true);
        setDeleteError(null);
        try {
            const { data } = await apiClient.delete(`/api/admin/users/${deleteTarget._id}`, {
                data: deleteForm
            });
            setDeleteResult(data.auditLog);
            await fetchUsers();
        } catch (err) {
            setDeleteError(err.response?.data?.msg || 'Deletion failed');
        } finally {
            setDeleteBusy(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    const counts = previewData?.preview?.counts;

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">All Users ({users.length})</Typography>
                    <Button onClick={fetchUsers} size="small">Refresh</Button>
                </Box>

                <Alert severity="warning" sx={{ mb: 2 }}>
                    Deactivating a user blocks login but keeps all data. Deleting permanently removes
                    the user's company and all bids/projects/feedback per Policy v1.1. ToS and consent
                    logs are always retained with denormalized identifiers.
                </Alert>

                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Company</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Last Login</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">No users.</TableCell>
                                </TableRow>
                            ) : users.map(u => {
                                const self = isSelf(u);
                                return (
                                    <TableRow key={u._id} hover>
                                        <TableCell>{u.name}</TableCell>
                                        <TableCell>{u.email}</TableCell>
                                        <TableCell>
                                            {u.company?.name || '—'}
                                            {u.company?.domain && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    {u.company.domain}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{u.role}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={u.isActive === false ? 'Deactivated' : 'Active'}
                                                color={u.isActive === false ? 'default' : 'success'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {u.createdAt ? format(new Date(u.createdAt), 'MMM d, yyyy') : '—'}
                                        </TableCell>
                                        <TableCell>
                                            {u.lastEmailLoginAt ? format(new Date(u.lastEmailLoginAt), 'MMM d, yyyy') : 'Never'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {self ? (
                                                <Chip size="small" label="You" variant="outlined" />
                                            ) : (
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    {u.isActive === false ? (
                                                        <Button
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                            onClick={() => setActionTarget({ user: u, action: 'reactivate' })}
                                                        >
                                                            Reactivate
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                            onClick={() => setActionTarget({ user: u, action: 'deactivate' })}
                                                        >
                                                            Deactivate
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                        onClick={() => openDelete(u)}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Stack>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>

            {/* Deactivate / Reactivate confirm dialog */}
            <Dialog open={!!actionTarget} onClose={() => !actionBusy && setActionTarget(null)}>
                <DialogTitle>
                    {actionTarget?.action === 'deactivate' ? 'Deactivate user?' : 'Reactivate user?'}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {actionTarget?.action === 'deactivate'
                            ? `This will block ${actionTarget?.user?.email} from logging in. They can be reactivated later. All data is preserved.`
                            : `This will allow ${actionTarget?.user?.email} to log in again.`}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setActionTarget(null)} disabled={actionBusy}>Cancel</Button>
                    <Button
                        onClick={runSimpleAction}
                        disabled={actionBusy}
                        color={actionTarget?.action === 'deactivate' ? 'warning' : 'success'}
                        variant="contained"
                    >
                        {actionBusy ? <CircularProgress size={20} /> : (actionTarget?.action === 'deactivate' ? 'Deactivate' : 'Reactivate')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete dialog */}
            <Dialog open={!!deleteTarget} onClose={() => !deleteBusy && closeDelete()} maxWidth="md" fullWidth>
                <DialogTitle>
                    Permanently delete user{deleteTarget ? `: ${deleteTarget.email}` : ''}
                </DialogTitle>
                <DialogContent dividers>
                    {previewLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {previewError && <Alert severity="error" sx={{ mb: 2 }}>{previewError}</Alert>}

                    {deleteResult ? (
                        <Box>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                Deletion complete. Audit log written.
                            </Alert>
                            <Typography variant="subtitle2" gutterBottom>Phase summary</Typography>
                            <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, fontSize: 12, overflow: 'auto' }}>
                                {JSON.stringify(deleteResult.phases, null, 2)}
                            </pre>
                            <Typography variant="caption" color="text.secondary">
                                Audit log: {deleteResult.auditLogFile}
                            </Typography>
                        </Box>
                    ) : previewData && (
                        <Box>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <strong>This is irreversible.</strong> The user's entire company and all associated data
                                will be deleted. ToS and consent logs are retained with denormalized identifiers per
                                Policy v1.1 Section 6.
                            </Alert>

                            <Typography variant="subtitle2" gutterBottom>
                                Company: {previewData.preview.company.name} ({previewData.preview.company.domain})
                                {previewData.preview.company.isProtected && (
                                    <Chip label="PROTECTED DOMAIN" color="error" size="small" sx={{ ml: 1 }} />
                                )}
                            </Typography>

                            {counts && (
                                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow><TableCell>ToS logs (denormalized, retained)</TableCell><TableCell align="right">{counts.phase1_auditSnapshot.tosAcceptanceLogs}</TableCell></TableRow>
                                            <TableRow><TableCell>Consent logs (denormalized, retained)</TableCell><TableCell align="right">{counts.phase1_auditSnapshot.consentLogs}</TableCell></TableRow>
                                            <TableRow><TableCell>Bid feedbacks</TableCell><TableCell align="right">{counts.phase2_feedbackChain.bidFeedbacks}</TableCell></TableRow>
                                            <TableRow><TableCell>Feedback requests</TableCell><TableCell align="right">{counts.phase2_feedbackChain.feedbackRequests}</TableCell></TableRow>
                                            <TableRow><TableCell>Feedback audit logs</TableCell><TableCell align="right">{counts.phase2_feedbackChain.feedbackAuditLogs}</TableCell></TableRow>
                                            <TableRow><TableCell>Inbound emails</TableCell><TableCell align="right">{counts.phase3_emailAndNotifications.inboundEmails}</TableCell></TableRow>
                                            <TableRow><TableCell>Notification logs</TableCell><TableCell align="right">{counts.phase3_emailAndNotifications.notificationLogs}</TableCell></TableRow>
                                            <TableRow><TableCell>Bids</TableCell><TableCell align="right">{counts.phase4_bidsAndCrm.bids}</TableCell></TableRow>
                                            <TableRow><TableCell>Projects</TableCell><TableCell align="right">{counts.phase4_bidsAndCrm.projects}</TableCell></TableRow>
                                            <TableRow><TableCell>GC contacts</TableCell><TableCell align="right">{counts.phase4_bidsAndCrm.gcContacts}</TableCell></TableRow>
                                            <TableRow><TableCell>General contractors</TableCell><TableCell align="right">{counts.phase4_bidsAndCrm.generalContractors}</TableCell></TableRow>
                                            <TableRow><TableCell>Email templates</TableCell><TableCell align="right">{counts.phase5_companyConfig.emailTemplates}</TableCell></TableRow>
                                            <TableRow><TableCell>Custom field defs</TableCell><TableCell align="right">{counts.phase5_companyConfig.customFieldDefinitions}</TableCell></TableRow>
                                            <TableRow><TableCell>User invitations</TableCell><TableCell align="right">{counts.phase5_companyConfig.userInvitations}</TableCell></TableRow>
                                            <TableRow><TableCell>Users</TableCell><TableCell align="right">{counts.phase6_usersAndCompany.users}</TableCell></TableRow>
                                            <TableRow><TableCell>Logo blob</TableCell><TableCell align="right">{counts.phase6_usersAndCompany.logoBlob}</TableCell></TableRow>
                                            <TableRow><TableCell>Company</TableCell><TableCell align="right">{counts.phase6_usersAndCompany.company}</TableCell></TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle2" gutterBottom>Authorization (Policy v1.1)</Typography>
                            {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
                            <Stack spacing={2}>
                                <TextField
                                    label="Authorized by (name)"
                                    value={deleteForm.authorizedBy}
                                    onChange={e => setDeleteForm(f => ({ ...f, authorizedBy: e.target.value }))}
                                    disabled={deleteBusy}
                                    fullWidth
                                    size="small"
                                />
                                <TextField
                                    label="Authorized by (email)"
                                    type="email"
                                    value={deleteForm.authorizedByEmail}
                                    onChange={e => setDeleteForm(f => ({ ...f, authorizedByEmail: e.target.value }))}
                                    disabled={deleteBusy}
                                    fullWidth
                                    size="small"
                                />
                                <TextField
                                    select
                                    label="Verification method"
                                    value={deleteForm.verificationMethod}
                                    onChange={e => setDeleteForm(f => ({ ...f, verificationMethod: e.target.value }))}
                                    disabled={deleteBusy}
                                    fullWidth
                                    size="small"
                                >
                                    {VERIFICATION_METHODS.map(m => (
                                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    label="Reason"
                                    value={deleteForm.reason}
                                    onChange={e => setDeleteForm(f => ({ ...f, reason: e.target.value }))}
                                    disabled={deleteBusy}
                                    fullWidth
                                    multiline
                                    rows={2}
                                    size="small"
                                    placeholder="e.g., Admin requested deletion via email on YYYY-MM-DD"
                                />
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDelete} disabled={deleteBusy}>
                        {deleteResult ? 'Close' : 'Cancel'}
                    </Button>
                    {!deleteResult && (
                        <Button
                            onClick={submitDelete}
                            disabled={deleteBusy || !previewData || !canSubmitDelete()}
                            color="error"
                            variant="contained"
                        >
                            {deleteBusy ? <CircularProgress size={20} /> : 'Permanently delete'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default SuperAdminUsersTable;
